import { useState } from 'react';
import { Calendar, Plus, Trash2 } from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { Badge } from '../components/Badge.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { useConfirm } from '../components/ConfirmDialog.tsx';
import { useToast } from '../components/Toast.tsx';
import { Reveal } from '../components/motion.tsx';
import './Holidays.css';

export function Holidays() {
  const list = trpc.holiday.list.useQuery();
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const toast = useToast();
  const upsert = trpc.holiday.upsert.useMutation({
    onSuccess: () => utils.holiday.list.invalidate(),
  });
  const remove = trpc.holiday.remove.useMutation({
    onSuccess: () => utils.holiday.list.invalidate(),
  });
  const [showForm, setShowForm] = useState(false);

  async function onDelete(id: string, name: string) {
    const ok = await confirm({
      title: 'Delete holiday?',
      body: (
        <>
          This removes <strong>{name}</strong> from your access policy.
        </>
      ),
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await remove.mutateAsync({ id });
    toast.success('Holiday deleted', name);
  }

  return (
    <div className="stack holidays-page">
      <Reveal>
        <header className="page-head">
          <div className="title">
            <span className="section-eyebrow">Access · Calendar</span>
            <h1>
              <span className="serif-accent">Holidays</span>
            </h1>
            <p className="muted">Days where access is blocked or schedules are overridden.</p>
          </div>
          <span className="spacer" />
          <button onClick={() => setShowForm(true)}>
            <Plus size={14} />
            New holiday
          </button>
        </header>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="card">
          {!list.data || list.data.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="No holidays"
              body="The A22K supports up to 100 holidays. Add them here to keep your access policy aligned."
              action={
                <button onClick={() => setShowForm(true)}>
                  <Plus size={14} />
                  New holiday
                </button>
              }
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Date</th>
                  <th>Repeats</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((h) => (
                  <tr key={h.id}>
                    <td className="cell-name">{h.name}</td>
                    <td className="mono">{h.date}</td>
                    <td>
                      {h.repeatYearly ? <Badge tone="info">yearly</Badge> : <Badge>once</Badge>}
                    </td>
                    <td>
                      <button
                        className="ghost"
                        aria-label={`Delete holiday ${h.name}`}
                        onClick={() => void onDelete(h.id, h.name)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Reveal>

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="New holiday"
        maxWidth={460}
        dismissable={!upsert.isPending}
        footer={
          <>
            <button type="button" className="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" form="holiday-form" disabled={upsert.isPending}>
              Save
            </button>
          </>
        }
      >
        <form
          id="holiday-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const name = String(fd.get('name'));
            await upsert.mutateAsync({
              name,
              date: String(fd.get('date')),
              repeatYearly: fd.get('repeatYearly') === 'on',
            });
            setShowForm(false);
            toast.success('Holiday saved', name);
          }}
          className="stack"
        >
          <label className="field">
            <span className="field-label">Name</span>
            <input name="name" placeholder="Name (e.g. Christmas Day)" required />
          </label>
          <label className="field">
            <span className="field-label">Date</span>
            <input name="date" type="date" required />
          </label>
          <label className="row repeat-toggle" style={{ gap: 8 }}>
            <input name="repeatYearly" type="checkbox" defaultChecked style={{ width: 'auto' }} />
            <span>Repeats every year</span>
          </label>
        </form>
      </Modal>
    </div>
  );
}
